import React, { useMemo, useState } from 'react'
import { InputProps } from './types'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'

/**
 * Componente Input minimalista con diseño limpio y sutil
 * Enfoque en claridad y simplicidad visual
 */
const Input: React.FC<InputProps> = ({
  type = 'text',
  error = false,
  errorMessage,
  disabled = false,
  readOnly = false,
  isLoading = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  rightIconOnClick,
  className = '',
  size = 'md',
  mode,
  onKeyPress,
  onKeyDown,
  onKeyUp,
  onFocus,
  onBlur,
  maxLength,
  minLength,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false)

  // Determinar si está en modo readonly según mode
  const isReadOnly = useMemo(() => {
    if (readOnly) return true
    if (mode === 'view') return true
    return false
  }, [readOnly, mode])

  // Determinar si está deshabilitado
  const isDisabled = useMemo(() => {
    return disabled || isLoading
  }, [disabled, isLoading])

  // Clases base según tamaño - minimalista
  const sizeClasses = useMemo(() => {
    switch (size) {
      case 'sm':
        return 'px-2.5 py-1.5 text-sm'
      case 'lg':
        return 'px-3.5 py-2.5 text-base'
      default:
        return 'px-3 py-2 text-sm'
    }
  }, [size])

  // Clases para el contenedor del input
  const containerClasses = useMemo(() => {
    return `relative flex items-center ${className}`
  }, [className])

  // Clases para el input - diseño minimalista
  const inputClasses = useMemo(() => {
    // Base minimalista: sin sombras, bordes sutiles, transiciones suaves
    const baseClasses = `w-full rounded-md border transition-colors duration-150 focus:outline-none focus:ring-1 focus:ring-offset-0 ${sizeClasses}`
    
    // Padding según iconos
    const paddingClasses = LeftIcon ? (RightIcon ? 'pl-9 pr-9' : 'pl-9 pr-3') : (RightIcon ? 'pl-3 pr-9' : 'px-3')
    
    // Estados minimalistas - colores sutiles
    const stateClasses = isDisabled
      ? 'bg-gray-50 text-gray-500 cursor-not-allowed border-gray-200'
      : isReadOnly
      ? 'bg-gray-50 text-gray-700 border-gray-200 cursor-default'
      : error
      ? 'border-red-300 text-gray-900 focus:ring-red-400 focus:border-red-400 bg-white'
      : `border-gray-300 text-gray-900 focus:ring-blue-400 focus:border-blue-400 bg-white ${isFocused ? '' : 'hover:border-gray-400'}`
    
    return `${baseClasses} ${paddingClasses} ${stateClasses}`
  }, [sizeClasses, LeftIcon, RightIcon, isDisabled, isReadOnly, error, isFocused])

  // Clases para icono izquierdo - minimalista
  const leftIconClasses = useMemo(() => {
    if (isDisabled) return 'text-gray-400'
    if (error) return 'text-red-400'
    if (isFocused) return 'text-gray-600'
    return 'text-gray-400'
  }, [isDisabled, error, isFocused])

  // Clases para icono derecho - minimalista
  const rightIconClasses = useMemo(() => {
    if (isDisabled) return 'text-gray-400'
    if (error) return 'text-red-400'
    if (rightIconOnClick) return 'text-gray-500 hover:text-gray-700 cursor-pointer transition-colors duration-150'
    return 'text-gray-400'
  }, [isDisabled, error, rightIconOnClick])

  // Handlers
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    if (onFocus) onFocus(e)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    if (onBlur) onBlur(e)
  }

  return (
    <div className={containerClasses}>
      {/* Icono izquierdo */}
      {LeftIcon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
          <LeftIcon className={`h-4 w-4 ${leftIconClasses}`} />
        </div>
      )}

      {/* Input minimalista */}
      <input
        type={type}
        disabled={isDisabled}
        readOnly={isReadOnly}
        maxLength={maxLength}
        minLength={minLength}
        onKeyPress={onKeyPress}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onFocus={handleFocus}
        onBlur={handleBlur}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={error}
        className={inputClasses}
        {...rest}
      />

      {/* Spinner de carga - minimalista */}
      {isLoading && !RightIcon && (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none z-10">
          <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-gray-400 border-t-transparent"></div>
        </div>
      )}

      {/* Icono derecho */}
      {RightIcon && !isLoading && (
        <div
          className={`absolute inset-y-0 right-0 pr-3 flex items-center z-10 ${
            rightIconOnClick ? 'pointer-events-auto' : 'pointer-events-none'
          }`}
          onClick={rightIconOnClick}
        >
          <RightIcon className={`h-4 w-4 ${rightIconClasses}`} />
        </div>
      )}

      {/* Mensaje de error - minimalista */}
      {error && errorMessage && (
        <div className="absolute -bottom-5 left-0 flex items-center gap-1 mt-1">
          <ExclamationCircleIcon className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
          <span className="text-xs text-red-600">{errorMessage}</span>
        </div>
      )}
    </div>
  )
}

export default React.memo(Input)
